import React from "react";
import { ArrowLeft, FileText, Smartphone } from "lucide-react";

/**
 * ReviewView — Step shown between filling the form and OTP.
 * Customer verifies their entered data + accepts T&C, then clicks
 * "Confirmar identidad" which triggers the OTP modal in the parent.
 */
export default function ReviewView({
  form,
  currentPlan,
  address,
  unit,
  city,
  zip,
  esiidParam,
  utilityParam,
  estimate,
  termsAccepted,
  setTermsAccepted,
  onBack,
  onContinue,
}) {
  const monthly = estimate?.annual_kwh
    ? Math.max(0, Math.round((currentPlan.rate / 100) * (estimate.annual_kwh / 12)))
    : null;

  return (
    <div className="max-w-[820px] mx-auto" data-testid="enroll-review">
      <div className="mb-6 text-center">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-velcor-green/10 px-3 py-1 text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-velcor-green">
          <FileText className="w-3 h-3" /> Revisión final
        </span>
        <h2
          className="display text-velcor-ink mt-3 mb-2"
          style={{ fontSize: "clamp(26px, 3.4vw, 36px)", fontWeight: 600, letterSpacing: "-0.022em" }}
        >
          Revisa tu información
        </h2>
        <p className="text-slate-500 text-[13.5px] max-w-[480px] mx-auto leading-relaxed">
          Verifica que todo esté correcto. En el siguiente paso confirmaremos tu identidad con un código SMS.
        </p>
      </div>

      <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        <ReviewBlock title="Datos personales" testid="review-block-personal">
          <ReviewRow label="Nombre completo" value={`${form.nombre} ${form.apellido}`} />
          <ReviewRow label="Correo electrónico" value={form.email} />
          <ReviewRow
            label="Teléfono"
            value={(form.telefono || "")
              .replace(/\D+/g, "")
              .replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")}
            mono
          />
          <ReviewRow label="Fecha de nacimiento" value={form.fecha_nacimiento || "—"} />
          {form.referral_code && (
            <ReviewRow label="Código de referido" value={form.referral_code} mono highlight />
          )}
        </ReviewBlock>

        <ReviewBlock title="Dirección de servicio" testid="review-block-service">
          <ReviewRow
            label="Dirección"
            value={`${address || `ZIP ${zip}`}${unit ? ` · #${unit}` : ""}`}
          />
          {city && <ReviewRow label="Ciudad / ZIP" value={`${city}, TX ${zip}`} />}
          {utilityParam && <ReviewRow label="Compañía local (TDU)" value={utilityParam} />}
          {esiidParam && <ReviewRow label="ESIID" value={esiidParam} mono />}
          <ReviewRow label="Fecha de inicio" value={form.fecha_inicio || "—"} />
        </ReviewBlock>

        <ReviewBlock title="Plan seleccionado" testid="review-block-plan">
          <ReviewRow label="Plan" value={currentPlan.name} />
          <ReviewRow label="Término" value={currentPlan.term} />
          <ReviewRow label="Tarifa fija" value={`${currentPlan.rate}¢ / kWh`} mono />
          {monthly !== null && (
            <ReviewRow label="Estimado mensual" value={`$${monthly} / mes`} mono />
          )}
        </ReviewBlock>
      </div>

      {/* Terms & Conditions */}
      <label
        className="mt-6 flex items-start gap-3 p-4 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50 cursor-pointer"
        data-testid="review-terms-row"
      >
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => setTermsAccepted(e.target.checked)}
          className="mt-0.5 w-4.5 h-4.5 accent-velcor-green"
          data-testid="review-terms-checkbox"
        />
        <div className="text-[13px] text-slate-700 leading-relaxed">
          <strong className="text-velcor-ink">
            Acepto los Términos del servicio y autorizo el cambio de proveedor.
          </strong>
          <span className="block text-[11.5px] text-slate-500 mt-1">
            Velcor Energy podrá enviar la solicitud a mi utility para iniciar el servicio. Puedo cancelar mi servicio en cualquier momento, sin penalidad. Consulta los documentos legales:{" "}
            <a
              href="/electricity-facts-label/flexible?from=enroll"
              className="text-velcor-green hover:underline font-bold"
            >
              EFL
            </a>{" "}
            ·{" "}
            <a href="/yrac?from=enroll" className="text-velcor-green hover:underline font-bold">
              YRAC
            </a>{" "}
            ·{" "}
            <a href="/terms?from=enroll" className="text-velcor-green hover:underline font-bold">
              Términos
            </a>
            .
          </span>
        </div>
      </label>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 h-12 px-5 rounded-full border-2 border-slate-200 hover:border-slate-300 text-slate-700 font-extrabold text-[13px]"
          data-testid="review-back"
        >
          <ArrowLeft className="w-4 h-4" /> Editar mis datos
        </button>
        <button
          type="button"
          onClick={onContinue}
          disabled={!termsAccepted}
          className="btn-green h-13 px-7 text-[14.5px] inline-flex items-center gap-2 disabled:opacity-50"
          data-testid="review-continue"
        >
          <Smartphone className="w-4.5 h-4.5" />
          Confirmar identidad
        </button>
      </div>
    </div>
  );
}

function ReviewBlock({ title, children, testid }) {
  return (
    <div className="border-b border-slate-100 last:border-b-0" data-testid={testid}>
      <div className="px-5 md:px-6 pt-5 pb-1.5">
        <div className="text-[10.5px] font-extrabold uppercase tracking-[0.18em] text-velcor-green">
          {title}
        </div>
      </div>
      <div className="px-5 md:px-6 pb-5 space-y-1.5">{children}</div>
    </div>
  );
}

function ReviewRow({ label, value, mono = false, highlight = false }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[12px] text-slate-500">{label}</span>
      <span
        className={`text-[13.5px] text-right font-semibold ${mono ? "font-mono tabular-nums" : ""} ${
          highlight ? "text-velcor-green" : "text-velcor-ink"
        }`}
      >
        {value || "—"}
      </span>
    </div>
  );
}
